import { ReactElement, useEffect, useMemo } from 'react'
import { getCommentOrientation, getCommentTextOrientations } from '@constants'
import { Vector3 } from 'three'
import {
  getPdfLabelBoxSizeWorld,
  getPdfLabelPaddingWorld,
  getPdfTextWidthScale,
  PDF_COMMENT_TEXT_WIDTH_SCALE,
  PDF_LABEL_LINE_GAP_SINGLE_SIDED,
} from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/components/FloorplanModelLabels/pdfLabelBox'
import { useThree } from '@react-three/fiber'
import { usePlanLabelPdfMetrics } from '@editorHooks'
import CommentCalloutArrow from '../CommentCalloutArrow'
import { TEXT_3D_GLYPH_SIZE_FACTOR } from '../ModelElementLabels/constants'
import Text3DThree from '../Text3DThree'

// Small constant z-lifts to avoid z-fighting (callouts above the model, label
// above the callouts). Module-level so they aren't re-allocated per render.
const CALLOUT_GROUP_OFFSET = new Vector3(0, 0, 0.02)
const LABEL_GROUP_OFFSET = new Vector3(0, 0, 0.01)

interface Props {
  comment: Comment3D
  labelScale: number
  onClick?: (comment: Comment3D) => void
  /** Skip the callout arrows (e.g. when an interactive overlay renders them). */
  hideCallouts?: boolean
  /** Fires when a scene click misses this comment (used to deselect it). */
  onPointerMissed?: (event: MouseEvent) => void
  /**
   * Cursor to show while hovering the label. 'text' (the I-beam) signals that
   * clicking starts in-place editing; 'not-allowed' signals editing is blocked
   * (linked comments are edited via the position list). Undefined leaves the
   * cursor alone so the model mesh underneath controls it.
   */
  hoverCursor?: 'text' | 'not-allowed'
}

const Comment3D = ({
  comment,
  labelScale,
  onClick,
  hideCallouts = false,
  onPointerMissed,
  hoverCursor,
}: Props): ReactElement => {
  const gl = useThree(state => state.gl)

  const position = useMemo(
    () => new Vector3(comment.position.x, comment.position.y, comment.position.z),
    [comment.position],
  )

  // Reset the cursor if the comment is deselected/unmounted while still hovered
  // (pointer-out would otherwise never fire to clear it).
  useEffect(() => {
    if (!hoverCursor) return
    return () => {
      gl.domElement.style.cursor = 'auto'
    }
  }, [hoverCursor, gl])

  const color = comment.color

  const callouts = comment.callouts || []

  const textLines = useMemo(() => {
    return comment.text.split('\n')
  }, [comment.text])

  const { textXOrientation, textYOrientation } = useMemo(
    () => getCommentTextOrientations(getCommentOrientation(comment)),
    [comment],
  )

  // The exported plan draws comments through the same `make_label_V2` as the
  // element labels — mirror its box, paddings, line spacing and width scaling
  // so the scene comment matches the PDF comment.
  const metrics = usePlanLabelPdfMetrics()
  const marginWorld = getPdfLabelPaddingWorld(metrics)
  const textWidthScale = getPdfTextWidthScale(textLines, true, PDF_COMMENT_TEXT_WIDTH_SCALE)
  const boxSize = getPdfLabelBoxSizeWorld(textLines, metrics, true, PDF_COMMENT_TEXT_WIDTH_SCALE)

  return (
    <>
      <group position={CALLOUT_GROUP_OFFSET} onPointerMissed={onPointerMissed}>
        {!hideCallouts &&
          callouts.map((callout, index) => {
            if (callout.knee) {
              return (
                <group key={`callout-${comment.id}-${index}`}>
                  <CommentCalloutArrow
                    from={position}
                    to={new Vector3(callout.knee.x, callout.knee.y, callout.knee.z)}
                    color={color}
                    id={comment.id}
                    index={index * 2}
                    arrowHead={false}
                  />
                  <CommentCalloutArrow
                    from={new Vector3(callout.knee.x, callout.knee.y, callout.knee.z)}
                    to={new Vector3(callout.end.x, callout.end.y, callout.end.z)}
                    color={color}
                    id={comment.id}
                    index={index * 2 + 1}
                    arrowHead
                  />
                </group>
              )
            }
            return (
              <CommentCalloutArrow
                key={`callout-${comment.id}-${index}`}
                from={position}
                to={new Vector3(callout.end.x, callout.end.y, callout.end.z)}
                color={color}
                id={comment.id}
                index={index}
                arrowHead
              />
            )
          })}

        <group
          position={LABEL_GROUP_OFFSET}
          onPointerOver={
            hoverCursor
              ? event => {
                  // Claim the hover so the model mesh underneath doesn't also
                  // swap the cursor (it sets a 'pointer' cursor on hover).
                  event.stopPropagation()
                  gl.domElement.style.cursor = hoverCursor
                }
              : undefined
          }
          onPointerOut={
            hoverCursor
              ? () => {
                  gl.domElement.style.cursor = 'auto'
                }
              : undefined
          }
        >
          {comment.showTitle && textLines.length > 0 && (
            <Text3DThree
              textLines={textLines}
              color={color}
              backgroundColor={comment.backgroundColor ?? '#FFFFFF'}
              borderColor={color}
              borderWidth={0.01}
              position={position}
              scale={labelScale}
              anchorLocation="center"
              opacity={1.0}
              margin={marginWorld}
              singleSidedLineGapFactor={PDF_LABEL_LINE_GAP_SINGLE_SIDED}
              textWidthScale={textWidthScale}
              boxSize={boxSize}
              glyphSizeFactor={1 / TEXT_3D_GLYPH_SIZE_FACTOR}
              textXOrientation={textXOrientation}
              textYOrientation={textYOrientation}
              onClick={
                onClick
                  ? event => {
                      // Prevent the click from reaching the model mesh underneath
                      // (which would select the underlying element).
                      event.stopPropagation()
                      onClick(comment)
                    }
                  : undefined
              }
            />
          )}
        </group>
      </group>
    </>
  )
}

export default Comment3D
